/**
 * ProspectResponsesList Component - MA-ARCH-163
 * 
 * Extracted from ScriptEdit.jsx - List of prospect response options for a node.
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Trash2 } from 'lucide-react';
import { getNodeLabel } from '../utils/scriptEditUtils';

/**
 * @param {Object} props
 * @param {Array} props.options - Current node options
 * @param {Array} props.nodes - Available target nodes
 * @param {Object} props.nodePositionById - Node position map
 * @param {boolean} props.canEdit - Whether user can edit
 * @param {Function} props.onUpdateOption - Callback: (optionId, patch) => void
 * @param {Function} props.onDeleteOption - Callback: (optionId) => void
 */
export function ProspectResponsesList({
  options = [],
  nodes = [],
  nodePositionById = {},
  canEdit,
  onUpdateOption,
  onDeleteOption,
}) {
  if (options.length === 0) {
    return (
      <div className="muted" data-testid="no-options-text">
        No prospect responses yet. Add at least one to create your branching.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="options-list">
      {options.map((o) => (
        <div
          key={o.id}
          className="rounded-2xl border border-white/10 bg-white/5 p-4"
          data-testid={`option-row-${o.id}`}
        >
          <div className="grid grid-cols-1 gap-3 md:grid-cols-12 md:items-end">
            <div className="space-y-2 md:col-span-6">
              <label className="text-sm text-slate-200/80" data-testid={`option-prospect-label-${o.id}`}>
                Prospect says
              </label>
              <Input
                defaultValue={o.prospect_says}
                onBlur={(e) =>
                  canEdit
                    ? onUpdateOption(o.id, { prospect_says: e.target.value })
                    : null
                }
                disabled={!canEdit}
                className="bg-white/5 text-slate-100 ring-1 ring-white/10"
                data-testid={`option-prospect-input-${o.id}`}
              />
            </div>

            <div className="space-y-2 md:col-span-5">
              <label className="text-sm text-slate-200/80" data-testid={`option-target-label-${o.id}`}>
                Next node
              </label>
              <Select
                defaultValue={o.target_node_id}
                onValueChange={(val) =>
                  canEdit
                    ? onUpdateOption(o.id, { target_node_id: val })
                    : null
                }
                disabled={!canEdit}
              >
                <SelectTrigger
                  className="bg-white/5 text-slate-100 ring-1 ring-white/10"
                  data-testid={`option-target-select-${o.id}`}
                >
                  <SelectValue placeholder="Choose node" />
                </SelectTrigger>
                <SelectContent data-testid={`option-target-select-content-${o.id}`}>
                  {nodes.map((n) => (
                    <SelectItem
                      key={n.node.id}
                      value={n.node.id}
                      data-testid={`option-target-item-${o.id}-${n.node.id}`}
                    >
                      <span className="text-xs text-slate-200/60">
                        #{nodePositionById[n.node.id] || "—"}
                      </span>
                      <span className="ml-2">{getNodeLabel(n.node)}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="md:col-span-1">
              <Button
                variant="secondary"
                size="icon"
                className={
                  "h-10 w-10 rounded-full bg-white/10 ring-1 ring-white/10 hover:bg-white/15 " +
                  (canEdit ? "text-rose-300" : "text-rose-300/50")
                }
                onClick={() => (canEdit ? onDeleteOption(o.id) : null)}
                disabled={!canEdit}
                data-testid={`delete-option-button-${o.id}`}
                title="Delete option"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="mt-3 flex items-center gap-2 text-xs text-slate-200/60" data-testid={`option-meta-${o.id}`}>
            <span>Targets:</span>
            <span className="font-mono text-slate-200/70" data-testid={`option-target-id-${o.id}`}>
              {o.target_node_id}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

export default ProspectResponsesList;
